import { useMemo } from 'react';
import { Project, WorkspaceGroups } from '@/types';
import { EProjectStatus } from '@/enums';
import useTranslation from './useTranslation';

interface UseWorkspaceDeletionWarningsParams {
  projects: Project[];
  isLastOwnedWorkspace: boolean;
}

interface UseOrganizationDeletionWarningsParams {
  workspaceGroups?: WorkspaceGroups;
  isLastOrganization: boolean;
}

/**
 * Hook for workspace deletion warnings
 * Returns a single warning message or null if no warnings
 */
export const useWorkspaceDeletionWarnings = ({
  projects,
  isLastOwnedWorkspace,
}: UseWorkspaceDeletionWarningsParams) => {
  const { lang } = useTranslation();

  return useMemo(() => {
    if (isLastOwnedWorkspace) return lang.get('msg.lastWorkspaceError');

    // Priority order: PUBLISHED > REQUEST_APPROVED > WAITING_APPROVAL
    let hasApproved = false;
    let hasPending = false;

    const hasPublished = projects.some((project) => project.status === EProjectStatus.PUBLISHED);

    if (hasPublished) {
      return lang.get('msg.deleteWorkspaceWarningPublished');
    }

    hasApproved = projects.some((project) => project.status === EProjectStatus.REQUEST_APPROVED);
    if (hasApproved) return lang.get('msg.deleteWorkspaceWarningApproved');

    hasPending = projects.some((project) => project.status === EProjectStatus.WAITING_APPROVAL);
    if (hasPending) return lang.get('msg.deleteWorkspaceWarningPending');

    return null;
  }, [projects, isLastOwnedWorkspace, lang]);
};

/**
 * Hook for organization deletion warnings
 * Returns a single warning message or null if no warnings
 */
export const useOrganizationDeletionWarnings = ({
  isLastOrganization,
}: UseOrganizationDeletionWarningsParams) => {
  const { lang } = useTranslation();

  return useMemo(() => {
    if (isLastOrganization) {
      return lang.get('msg.lastOrganizationWarning');
    }

    return null;
  }, [isLastOrganization, lang]);
};
